import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Lock, Clock, Play, ChevronRight, User, Mail, Star, Calendar, Sparkles, Settings, LogOut, ChevronDown, FileText, ClipboardList, Info } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ProgramPreviewModal from '../components/ProgramPreviewModal';
import AccountSettings from '../components/AccountSettings';
import TeamProgramCards from '../components/TeamProgramCards';

// Program data
const programs = [
  {
    id: 'flex-detox',
    title: 'The Flex Detox Program',
    subtitle: 'A 21-day Holistic Detox — choose your depth',
    description: 'Eat with the colors. Move with your body. Choose your depth. Seven system-aligned phases over 21 days, with three tiers of practice (Glow Up → Deep Detox → Full Immersion) and an optional 7-day Full Spectrum bonus week.',
    image: 'https://images.unsplash.com/photo-1526779259212-939e64788e3c?w=800',
    lessons: 21,
    duration: '21–28 days',
    status: 'active',
    openAccess: true,
    topics: ['System Eating', 'Detox', 'Personalized Tiers', 'Holistic'],
  },
  {
    id: 'back-reset',
    title: 'The Back Reset Program',
    subtitle: 'Connecting and Reconnecting to our bodies deeper wisdom for healing',
    description: 'A comprehensive program designed to help you reconnect with your body\'s innate healing wisdom. Through guided lessons and gentle practices, you\'ll learn to release tension, restore mobility, and develop a loving relationship with your back.',
    image: 'https://customer-assets.emergentagent.com/job_greeting-hub-217/artifacts/rzz31rvw_R%20%282%29.jpg',
    lessons: 16,
    duration: 'Self-paced',
    status: 'active',
    topics: ['Back Health', 'Body Wisdom', 'Healing', 'Movement'],
    hasPreview: false,
    openAccess: true,
    comingSoonBadge: true,
  },
  {
    id: 'double-down',
    title: 'The Double Down Program',
    subtitle: 'A shake up, wake up for your metabolic system',
    description: "Two Fascial Maneuver practices per day (Zoom-led or follow-along video) paired with cleansing recipes, meal plans, and vitality-boosting elixirs. Not extreme — dedicated. Available stand-alone or as an add-on to your Flex Detox tier.",
    image: 'https://customer-assets-cm19k8pv.emergentagent.net/job_f11ad4fb-d6a4-4f13-b6d9-cc4ad6bfdd79/artifacts/hah5fcb4_ChatGPT%20Image%20Jul%2029%2C%202026%2C%2012_45_51%20PM.png',
    lessons: 21,
    duration: 'Daily practice',
    status: 'active',
    openAccess: true,
    topics: ['Fascial Maneuvers', 'Cleansing', 'Vitality', 'Metabolic Reset'],
  },
  {
    id: 'fascia-reset',
    title: '28 Day Stress Reset',
    subtitle: 'Feel the Fascia Miracle and Kick Start Healing at a deeper level',
    description: 'Discover the incredible world of fascia and how this connective tissue system impacts every aspect of your health. Over 28 days, you\'ll learn targeted techniques to release fascial restrictions and restore fluid movement.',
    image: 'https://customer-assets.emergentagent.com/job_greeting-hub-217/artifacts/gzscn1cb_WhatsApp%20Image%202026-02-11%20at%206.46.53%20PM.jpeg',
    lessons: 31,
    duration: '28 days',
    status: 'active',
    topics: ['Fascia', 'Connective Tissue', 'Deep Healing', 'Transformation'],
    promoDate: 'May 1 – May 28',
    promoTag: 'Early Bird Available',
    hasPreview: true,
    bookingUrl: 'https://www.wellnessliving.com/rs/event/breathing_room-rws2se?k_class=957840&k_class_tab=75840',
  },
  {
    id: 'terrain',
    title: 'SLOW WELLNESS',
    subtitle: 'Tapping into your Terrain',
    description: 'Real transformation takes time. This comprehensive year-long journey will guide you through rebuilding your health from the ground up—addressing sleep, nutrition, movement, stress, and the deeper aspects of wellbeing that most programs miss.',
    image: 'https://images.unsplash.com/photo-1518495973542-4542c06a5843?w=800',
    lessons: 52,
    duration: '12 months',
    status: 'active',
    hasPreview: true,
    topics: ['Long-term Wellness', 'Foundations', 'Transformation'],
    featuredTool: 'Wellness Wheel Assessment',
  },
  {
    id: 'crap-cleanse',
    title: '14-Day C.R.A.P. Cleanse',
    subtitle: 'Getting the C.R.A.P. out of your life',
    description: 'A no-fad, nothing extreme, no starving cleanse. C.R.A.P. stands for Carbohydrate Rich, Refined, Artificial, Processed foods. For 14 days, fill your body with real, life-giving foods and feel the difference.',
    image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800',
    lessons: 16,
    duration: '14 days',
    status: 'active',
    topics: ['Nutrition', 'Cleanse', 'Real Food', 'Detox'],
    hasPreview: false,
  },
  {
    id: 'feel-good-detox',
    title: '21-Day Feel Good Detox',
    subtitle: 'Reset, energize and revitalize mind, body and soul',
    description: 'A holistic 21-day nutritional detox to spring life into your organs, energy and vitality. Complete with food guides, juicing recipes, liver flush protocol, and interactive progress tracking.',
    image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=800',
    lessons: 24,
    duration: '21 days',
    status: 'active',
    topics: ['Detox', 'Nutrition', 'Holistic Health', 'Cleanse'],
    hasPreview: false,
  },
  {
    id: 'life-ninja-game',
    title: 'The Life Ninja Game',
    subtitle: 'A Journey of Choice, a Practice for Life',
    description: "A living map of your wellness journey across 7 territories — Survive, Stabilize, Restore, Reconnect, Choose, Create, Thrive. Part Game of Life, part Snakes & Ladders. Step into each area at your own pace. This isn't a race, it's a practice.",
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/odrleuev_ChatGPT%20Image%20Sep%207%2C%202026%2C%2011_33_15%20AM.png',
    lessons: 40,
    duration: 'Self-paced',
    status: 'active',
    openAccess: true,
    topics: ['Life Map', 'Nervous System', 'Growth', 'Practice'],
  },
  {
    id: 'surviving-to-thriving',
    title: 'From Surviving to Thriving',
    subtitle: 'Move out of fight-or-flight into rest, digest & heal',
    description: "A calm, guided journey through your nervous system — understand why you're stuck in survival mode and learn the daily practices that signal safety, so your body can finally rest, digest and heal. The foundation beneath every other kind of wellness.",
    image: 'https://static.prod-images.emergentagent.com/jobs/9cc27ad2-9473-4d8d-951e-838ca155566b/images/22e742907bb5008642ca20ed843d8ca2b57aee6438af1e15ee580d20d2e0a398.jpeg',
    lessons: 7,
    duration: 'Self-paced',
    status: 'active',
    openAccess: true,
    topics: ['Nervous System', 'Regulation', 'Stress', 'Healing'],
  },
  {
    id: 'four-pillars',
    title: 'The Four Pillars',
    subtitle: 'A path to wellness — The Body, Mind, Emotional & Energetic',
    description: "The streamlined heart of the 9 Pillars work, distilled into four living layers. Meet the REV program (Regulate · Elevate · Vitalize) and learn to ask a softer, truer question: 'Where am I asking for support right now?' A one-year journey of attunement over discipline.",
    image: 'https://customer-assets-v7afamib.emergentagent.net/job_9cc27ad2-9473-4d8d-951e-838ca155566b/artifacts/w7o9r493_magic%20layers.png',
    lessons: 4,
    duration: 'Self-paced',
    status: 'active',
    openAccess: true,
    topics: ['Coaching', 'REV Program', 'Holistic', 'Growth'],
  },
  {
    id: 'fast-brain',
    title: 'The Spicy Brain Survival Guide',
    subtitle: 'Find where your brain is today — then take one tiny step',
    description: "Are you a super thinker, a fast brain, neuro-spicy — or just looking for more direction? Plan your way, without the drama, the fight, the lost momentum or squeezing yourself into how other people do it.",
    image: '/images/fast-brain/compass-card.jpg',
    lessons: 5,
    duration: 'Self-paced',
    status: 'active',
    topics: ['Focus', 'Overwhelm', 'Worksheets', 'Printable'],
  },
  {
    id: 'fifty2-tuesdays',
    title: 'Fifty2 Tuesdays',
    subtitle: 'A Companion for the Year',
    description: 'A quiet weekly practice for steadier living — 52 weeks of mindfulness, one casual Tuesday at a time. Each week brings reflection, insights and affirmations for our personal expansion. Like having friendly coffee with yourself.',
    image: '/images/fifty2-cover.png',
    lessons: 52,
    duration: 'Self-paced',
    status: 'active',
    freeIntro: true,
    topics: ['Mindfulness', 'Weekly Practice', 'Year Journey'],
  },
];

const ProgramsPage = () => {
  const { user, hasAccess, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [previewProgram, setPreviewProgram] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="bg-cream min-h-screen pt-24" data-testid="programs-page">
      {/* Hero Section */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Back Link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-forest/70 hover:text-forest transition-colors mb-8"
          >
            <ArrowLeft size={18} />
            <span className="font-body text-sm">Back to Home</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <p className="text-terracotta font-body text-sm tracking-widest uppercase mb-4">
              Premium Programs
            </p>
            <h1 className="font-heading text-5xl md:text-6xl text-forest leading-[1.1] mb-6">
              Transformational Programs
            </h1>
            <p className="text-stone-600 text-lg font-body leading-relaxed">
              Deep-dive programs designed to create lasting change. Each program includes 
              video lessons, written guides, and practical exercises to support your journey.
            </p>
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="max-w-3xl mx-auto mb-16"
            data-testid="programs-disclaimer"
          >
            <div className="bg-white/70 border border-forest/15 rounded-2xl p-6 md:p-7 flex gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-forest/10 text-forest flex-shrink-0">
                <Info size={20} />
              </div>
              <div>
                <p className="font-heading text-base text-forest mb-2">A note from Jill</p>
                <p className="font-body text-sm md:text-[15px] text-stone-600 leading-relaxed">
                  As a Holistic Health Practitioner and Restorative Personal Trainer, I provide
                  supportive holistic approaches for recovering, reversing, and potentially becoming
                  healthier than you ever thought you could be. This is <span className="font-medium text-forest">not medical
                  advice</span>, nor is it a replacement for medical care. The information shared here is offered
                  for your highest wellness. Please consult your doctor where appropriate.
                </p>
              </div>
            </div>
          </motion.div>

          {user ? (
            <div className="relative max-w-2xl mx-auto mb-8" ref={userMenuRef}>
              <motion.button
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="w-full bg-forest/5 hover:bg-forest/8 rounded-xl p-4 flex items-center justify-between transition-colors cursor-pointer"
                data-testid="welcome-bar"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-terracotta rounded-full flex items-center justify-center">
                    <span className="text-cream text-sm font-heading font-bold">
                      {user.name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="font-body text-forest font-medium">Welcome back, {user.name}!</p>
                    <p className="font-body text-stone-500 text-sm">
                      {user.unlocked_programs?.length || 0} program(s) unlocked
                    </p>
                  </div>
                </div>
                <ChevronDown size={18} className={`text-stone-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
              </motion.button>

              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-0 right-0 mt-2 bg-white rounded-xl shadow-lg border border-stone-100 overflow-hidden z-20"
                    data-testid="user-dropdown"
                  >
                    <div className="py-1">
                      <button
                        onClick={() => { setShowAccountSettings(true); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left font-body text-sm text-stone-600 hover:bg-stone-50 transition-colors"
                        data-testid="account-settings-btn"
                      >
                        <Settings size={16} className="text-stone-400" />
                        Account Settings
                      </button>
                      <button
                        onClick={() => { logout(); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-3 px-5 py-3 text-left font-body text-sm text-red-500 hover:bg-red-50 transition-colors"
                        data-testid="logout-btn"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-terracotta/10 rounded-xl p-6 mb-8 text-center max-w-2xl mx-auto"
            >
              <p className="text-forest font-body mb-3">
                <strong>Sign in</strong> to access your purchased programs
              </p>
              <button
                onClick={() => {
                  setAuthMode('login');
                  setShowAuthModal(true);
                }}
                className="bg-forest text-cream px-6 py-2 rounded-full font-body text-sm hover:bg-forest-dark transition-colors"
              >
                Sign In / Create Account
              </button>
            </motion.div>
          )}

          {/* ── NEW CLIENT INTAKE Section ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 bg-[#f1e8dd] border border-stone-200 rounded-2xl overflow-hidden"
            data-testid="new-client-section-card"
          >
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-2 bg-[#e9d9c4]/50 flex items-center justify-center p-6 md:p-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forest text-cream mb-3">
                    <FileText size={22} />
                  </div>
                  <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">
                    New Client Intake
                  </p>
                </div>
              </div>
              <div className="md:col-span-3 p-6 md:p-7">
                <h2 className="font-heading text-2xl md:text-3xl text-forest mb-2">
                  New Client Protocols
                </h2>
                <p className="text-stone-600 font-body text-sm mb-4 leading-relaxed">
                  Your landing pad to get started. A welcome note from Jill, waivers,
                  foundational questionnaires, and a quick intake form so Jill can prepare
                  for your first session.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {[
                    { icon: Play, label: 'Welcome Video' },
                    { icon: FileText, label: 'Waivers' },
                    { icon: ClipboardList, label: 'Questionnaires' },
                    { icon: Mail, label: 'Contact & Goals' },
                  ].map(({ icon: Icon, label }) => (
                    <div
                      key={label}
                      className="flex items-center gap-2 text-sm font-body text-stone-600 bg-white/70 border border-stone-200 rounded-lg px-3 py-1.5"
                    >
                      <Icon size={14} className="text-[#8a6a5a]" />
                      {label}
                    </div>
                  ))}
                </div>
                <Link
                  to="/programs/new-client"
                  className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-2.5 rounded-full hover:bg-forest-dark transition-colors"
                  data-testid="new-client-enter-link"
                >
                  Start Your Intake <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* ── V.I.P. Private Members Section ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mb-16 bg-[#f5efe4] border border-stone-200 rounded-2xl overflow-hidden"
            data-testid="vip-section-card"
          >
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-2 bg-forest/5 flex items-center justify-center p-6 md:p-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-forest text-cream mb-3">
                    <Sparkles size={22} />
                  </div>
                  <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">
                    Private Members
                  </p>
                </div>
              </div>
              <div className="md:col-span-3 p-6 md:p-7">
                <h2 className="font-heading text-2xl md:text-3xl text-forest mb-2">
                  V.I.P. Video Library
                </h2>
                <p className="text-stone-600 font-body text-sm mb-4 leading-relaxed">
                  A private space for Jill's in-person clients — a growing video library of
                  the healing routines we work through together, organized into four
                  categories:
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {['Restorative', 'Fascia Work', 'Functional Fitness', 'Natural Movement'].map(
                    (c) => (
                      <div
                        key={c}
                        className="text-sm font-body text-stone-600 bg-white/70 border border-stone-200 rounded-lg px-3 py-1.5"
                      >
                        {c}
                      </div>
                    )
                  )}
                </div>
                {user && hasAccess('vip') ? (
                  <Link
                    to="/programs/vip"
                    className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-2.5 rounded-full hover:bg-forest-dark transition-colors"
                    data-testid="vip-enter-link"
                  >
                    Enter V.I.P. Library <ChevronRight size={16} />
                  </Link>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    <Link
                      to="/programs/vip"
                      className="inline-flex items-center gap-2 bg-white border border-stone-300 text-forest font-body font-medium px-5 py-2 rounded-full hover:border-forest transition-colors"
                      data-testid="vip-learn-more-link"
                    >
                      <Lock size={14} /> Members Only
                    </Link>
                    <a
                      href="mailto:heyjbennett@yahoo.ca?subject=V.I.P. Membership Inquiry"
                      className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-5 py-2 rounded-full hover:bg-forest-dark transition-colors"
                      data-testid="vip-inquiry-link"
                    >
                      Inquire about V.I.P.
                    </a>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          <TeamProgramCards />

          {/* ── Discover More about Fascia (public) ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mb-16 bg-[#f5efe4] border border-stone-200 rounded-2xl overflow-hidden"
            data-testid="learn-fascia-section-card"
          >
            <div className="grid grid-cols-1 md:grid-cols-5">
              <div className="md:col-span-2 bg-terracotta/10 flex items-center justify-center p-6 md:p-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-terracotta text-white mb-3">
                    <Sparkles size={22} />
                  </div>
                  <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">
                    Free to Explore
                  </p>
                </div>
              </div>
              <div className="md:col-span-3 p-6 md:p-7">
                <h2 className="font-heading text-2xl md:text-3xl text-forest mb-2">
                  Discover More about Fascia
                </h2>
                <p className="text-stone-600 font-body text-sm mb-4 leading-relaxed">
                  Learn the power and logic of fascia — the continuous web of connective tissue that shapes and
                  connects your whole body — with an illustrated guide to the major fascial lines.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-4">
                  {['What Fascia Is', 'Why It Matters', 'The Fascial Lines', 'Illustrated Guide'].map((c) => (
                    <div
                      key={c}
                      className="text-sm font-body text-stone-600 bg-white/70 border border-stone-200 rounded-lg px-3 py-1.5"
                    >
                      {c}
                    </div>
                  ))}
                </div>
                <Link
                  to="/learn/fascia"
                  className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-2.5 rounded-full hover:bg-forest-dark transition-colors"
                  data-testid="learn-fascia-enter-link"
                >
                  Learn More about Fascia <ChevronRight size={16} />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Programs Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow"
              >
                {/* Program Image */}
                <div className="relative h-56">
                  <img
                    src={program.image}
                    alt={program.title}
                    className={`w-full h-full object-cover ${program.id === 'double-down' ? 'object-top' : ''}`}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${program.id === 'fast-brain' ? 'from-forest via-forest/60 to-transparent' : 'from-forest/80 to-transparent'}`} />
                  
                  {/* Status Badge */}
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    {program.comingSoonBadge && (
                      <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-body font-medium flex items-center gap-1">
                        <Clock size={12} />
                        Content Coming Soon
                      </span>
                    )}
                    {program.status === 'coming-soon' ? (
                      <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-body font-medium flex items-center gap-1">
                        <Clock size={12} />
                        Coming Soon
                      </span>
                    ) : program.openAccess ? (
                      <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-body font-medium flex items-center gap-1">
                        <Play size={12} />
                        Open Access
                      </span>
                    ) : program.freeIntro && !hasAccess(program.id) ? (
                      <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-body font-medium flex items-center gap-1" data-testid={`program-badge-${program.id}`}>
                        <Play size={12} />
                        Week 1 Free
                      </span>
                    ) : hasAccess(program.id) ? (
                      <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-body font-medium flex items-center gap-1">
                        <Play size={12} />
                        Unlocked
                      </span>
                    ) : (
                      <>
                        {/* Show promo badges for locked programs with special offers */}
                        {program.promoDate && (
                          <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-body font-medium flex items-center gap-1">
                            <Calendar size={12} />
                            {program.promoDate}
                          </span>
                        )}
                        {program.promoTag && (
                          <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-xs font-body font-medium flex items-center gap-1">
                            <Sparkles size={12} />
                            {program.promoTag}
                          </span>
                        )}
                        {!program.promoDate && !program.promoTag && (
                          <span className="bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-xs font-body font-medium flex items-center gap-1">
                            <Lock size={12} />
                            Purchase Program
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Title Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-heading text-2xl text-white mb-1">{program.title}</h3>
                    <p className="text-cream/80 font-body text-sm">{program.subtitle}</p>
                  </div>
                </div>

                {/* Program Info */}
                <div className="p-6">
                  <p className="text-stone-600 font-body text-sm mb-4 line-clamp-3">
                    {program.description}
                  </p>

                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-4 text-sm">
                    <div className="flex items-center gap-1 text-stone-500">
                      <Play size={14} className="text-terracotta" />
                      <span className="font-body">{program.lessons} lessons</span>
                    </div>
                    <div className="flex items-center gap-1 text-stone-500">
                      <Clock size={14} className="text-terracotta" />
                      <span className="font-body">{program.duration}</span>
                    </div>
                  </div>

                  {/* Topics */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {program.topics.slice(0, 3).map((topic) => (
                      <span
                        key={topic}
                        className="px-2 py-1 bg-stone-100 text-stone-600 rounded-full text-xs font-body"
                      >
                        {topic}
                      </span>
                    ))}
                    {program.featuredTool && (
                      <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-body font-medium flex items-center gap-1">
                        <Sparkles size={10} />
                        {program.featuredTool}
                      </span>
                    )}
                  </div>

                  {/* Flex Detox assessment pill */}
                  {program.id === 'flex-detox' && (
                    <Link
                      to="/quiz/body-assessment"
                      className="flex items-center justify-between gap-2 mb-4 px-4 py-2.5 rounded-full bg-gradient-to-r from-terracotta/10 to-emerald-50 border border-terracotta/30 text-forest hover:from-terracotta/20 transition-colors"
                      data-testid="flex-detox-assessment-pill"
                    >
                      <span className="inline-flex items-center gap-2 font-body text-sm font-medium">
                        <Sparkles size={14} className="text-terracotta" />
                        What Is Your Body Asking For?
                      </span>
                      <ChevronRight size={16} className="text-terracotta" />
                    </Link>
                  )}

                  {/* Action Buttons */}
                  {program.status === 'coming-soon' ? (
                    <button
                      disabled
                      className="w-full bg-stone-200 text-stone-500 py-3 rounded-full font-body font-medium cursor-not-allowed"
                    >
                      Coming Soon
                    </button>
                  ) : program.openAccess || program.freeIntro || hasAccess(program.id) || (program.id === 'crap-cleanse' && hasAccess('crap-cleanse-intro')) || (program.id === 'feel-good-detox' && hasAccess('feel-good-detox-intro')) ? (
                    <Link
                      to={`/programs/${program.id}`}
                      className="w-full bg-forest text-cream py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors flex items-center justify-center gap-2"
                    >
                      {program.openAccess ? 'Explore Program' : program.freeIntro && !hasAccess(program.id) ? 'Try Week 1 Free' : 'Start Learning'}
                      <ChevronRight size={18} />
                    </Link>
                  ) : (
                    <div className="flex gap-3">
                      {/* Learn More button for programs with preview */}
                      {program.hasPreview && (
                        <button
                          onClick={() => setPreviewProgram(program)}
                          className="flex-1 bg-stone-100 text-forest py-3 rounded-full font-body font-medium hover:bg-stone-200 transition-colors flex items-center justify-center gap-2"
                        >
                          Learn More
                        </button>
                      )}
                      {program.bookingUrl ? (
                        <a
                          href={program.bookingUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${program.hasPreview ? 'flex-1' : 'w-full'} bg-terracotta text-white py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors flex items-center justify-center gap-2`}
                        >
                          <ChevronRight size={16} />
                          Register
                        </a>
                      ) : (
                        <button
                          onClick={() => {
                            if (!user) {
                              setAuthMode('login');
                              setShowAuthModal(true);
                            } else {
                              if (program.hasPreview) {
                                setPreviewProgram(program);
                              } else {
                                alert('To purchase this program, please contact us at heyjbennett@yahoo.ca');
                              }
                            }
                          }}
                          className={`${program.hasPreview ? 'flex-1' : 'w-full'} bg-terracotta text-white py-3 rounded-full font-body font-medium hover:bg-terracotta-dark transition-colors flex items-center justify-center gap-2`}
                        >
                          <Mail size={16} />
                          Contact to Register
                        </button>
                      )}
                    </div>
                  )}

                  {/* Group / Self-Guided Availability Footnote */}
                  <p
                    className="mt-4 pt-4 border-t border-stone-100 font-body text-xs text-stone-500 italic leading-relaxed"
                    data-testid={`program-availability-${program.id}`}
                  >
                    Organized group course is run periodically through the year — dates will be announced.{' '}
                    <Link to="/contact" className="text-forest underline hover:text-terracotta not-italic">
                      Please contact
                    </Link>{' '}
                    for dates or requests. Self-guided program is available upon request.
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* How to Purchase Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-16 bg-forest text-cream rounded-2xl p-8 md:p-12"
          >
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-3xl mb-4">How to Purchase a Program</h2>
              <p className="text-cream/80 font-body mb-8">
                Our programs are purchased directly through our Bermuda-based business. 
                Here's how to get started:
              </p>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                <div className="bg-cream/10 rounded-xl p-5">
                  <div className="w-10 h-10 bg-terracotta rounded-full flex items-center justify-center mb-3">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h4 className="font-heading text-lg mb-2 text-terracotta">Create Account</h4>
                  <p className="text-cream/70 font-body text-sm">
                    Sign up with your email to track your programs
                  </p>
                </div>
                <div className="bg-cream/10 rounded-xl p-5">
                  <div className="w-10 h-10 bg-terracotta rounded-full flex items-center justify-center mb-3">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h4 className="font-heading text-lg mb-2 text-terracotta">Contact Us</h4>
                  <p className="text-cream/70 font-body text-sm">
                    Email heyjbennett@yahoo.ca to arrange payment
                  </p>
                </div>
                <div className="bg-cream/10 rounded-xl p-5">
                  <div className="w-10 h-10 bg-terracotta rounded-full flex items-center justify-center mb-3">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h4 className="font-heading text-lg mb-2 text-terracotta">Get Access</h4>
                  <p className="text-cream/70 font-body text-sm">
                    Once payment is confirmed, your program will be unlocked
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Program Preview Modal */}
      <ProgramPreviewModal 
        program={previewProgram}
        isOpen={!!previewProgram}
        onClose={() => setPreviewProgram(null)}
      />

      {/* Auth Modal */}
      <AnimatePresence>
        {showAuthModal && (
          <AuthModal
            mode={authMode}
            setMode={setAuthMode}
            onClose={() => setShowAuthModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Account Settings Modal */}
      <AccountSettings
        isOpen={showAccountSettings}
        onClose={() => setShowAccountSettings(false)}
      />
    </div>
  );
};

// Auth Modal Component
const AuthModal = ({ mode, setMode, onClose }) => {
  const { login, register } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-forest/80 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-cream rounded-2xl max-w-md w-full p-8"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="font-heading text-2xl text-forest mb-2">Forgot Password?</h2>
          <p className="text-stone-600 font-body text-sm mb-6">
            No worries! Please reach out to Jill and she'll reset your password for you.
          </p>
          <div className="bg-forest/5 rounded-xl p-4 mb-6">
            <p className="text-forest font-body text-sm mb-2">You can contact Jill via:</p>
            <a href="/contact" onClick={onClose} className="text-terracotta font-body text-sm hover:underline block">
              Contact Page
            </a>
          </div>
          <button
            onClick={() => setShowForgotPassword(false)}
            className="w-full bg-forest text-cream py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors"
          >
            Back to Sign In
          </button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-forest/80 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-cream rounded-2xl max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-heading text-2xl text-forest mb-2">
          {mode === 'login' ? 'Welcome Back' : 'Create Account'}
        </h2>
        <p className="text-stone-600 font-body text-sm mb-6">
          {mode === 'login' 
            ? 'Sign in to access your programs' 
            : 'Sign up to purchase and access programs'}
        </p>

        {error && (
          <div className="bg-red-100 text-red-700 px-4 py-2 rounded-lg mb-4 font-body text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <div>
              <label className="block text-forest font-body font-medium mb-1 text-sm">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-white border border-stone-300 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-terracotta"
                placeholder="Your name"
              />
            </div>
          )}
          
          <div>
            <label className="block text-forest font-body font-medium mb-1 text-sm">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full bg-white border border-stone-300 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-terracotta"
              placeholder="your@email.com"
            />
          </div>

          <div>
            <label className="block text-forest font-body font-medium mb-1 text-sm">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full bg-white border border-stone-300 rounded-xl px-4 py-3 text-forest focus:outline-none focus:border-terracotta"
              placeholder="••••••••"
            />
            {mode === 'login' && (
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="text-terracotta font-body text-xs mt-1.5 hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-forest text-cream py-3 rounded-full font-body font-medium hover:bg-forest-dark transition-colors disabled:opacity-50"
          >
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-terracotta font-body text-sm hover:underline"
          >
            {mode === 'login' 
              ? "Don't have an account? Sign up" 
              : 'Already have an account? Sign in'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProgramsPage;
